/**
 * THE ONLY PLACE THIS APP CALLS fetch().
 *
 * Rules this file exists to enforce:
 *  - one error path   : everything throws ApiError, nothing throws a raw Response
 *  - one 401 handler  : session loss is handled here, once, not in eleven screens
 *  - no token handling: auth is a same-origin httpOnly cookie, so the browser
 *                       carries it and the client never sees it
 *
 * If you are about to write fetch() somewhere else, add the call here instead.
 */

const BASE = '/api'

export class ApiError extends Error {
  /**
   * @param {number} status HTTP status, or 0 when the request never reached the server
   * @param {string} code   short machine code: network | unauthorized | http | parse
   * @param {string} message participant-safe message. No clinical content, ever.
   * @param {*} body        parsed response body when there was one
   */
  constructor(status, code, message, body = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.body = body
  }
}

/* ------------------------------------------------------------------ *
 * The single 401 handler.
 * App.jsx registers one function at mount; nothing else may.
 * ------------------------------------------------------------------ */

let unauthorizedHandler = null

export function setUnauthorizedHandler(fn) {
  unauthorizedHandler = fn
}

/* ------------------------------------------------------------------ *
 * Error message normalisation.
 * FastAPI sends {detail: "..."} or {detail: [{loc, msg, type}, ...]}.
 * Screens should be able to render err.message directly.
 * ------------------------------------------------------------------ */

/** Pydantic prefixes custom validators with "Value error, ". Participants should
 *  not read that, and it is the only cosmetic tidy applied to a server message. */
const tidy = (text) => text.replace(/^Value error,\s*/i, '')

/**
 * "Please try again" is wrong when the server has just said to wait four
 * minutes. Same class as the consent screen's retry that could never work: an
 * instruction that fails if followed. The server sends retry_after_seconds, so
 * the screen can say how long instead of implying "now".
 */
function rateLimitMessage(detail) {
  const seconds = Number(detail?.retry_after_seconds)
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return 'Too many attempts from this connection. Please wait a few minutes and try again.'
  }
  const minutes = Math.ceil(seconds / 60)
  const wait = minutes <= 1 ? 'about a minute' : `about ${minutes} minutes`
  return `Too many attempts from this connection. Please wait ${wait} and try again.`
}

function messageFromBody(body, status) {
  if (body && typeof body === 'object') {
    const d = body.detail
    if (d && typeof d === 'object' && d.code === 'rate_limited') return rateLimitMessage(d)
    if (typeof d === 'string' && d.trim()) return tidy(d)
    if (Array.isArray(d) && d.length) {
      const first = d[0]
      // Only surface a validation message when it is about something the
      // participant actually typed, i.e. a field in the request body. A path or
      // query error means a malformed URL — a bad beep id from a stale
      // notification, say — and its message is parser internals:
      // "Input should be a valid UUID, invalid character: found `n` at 1".
      // Showing that to someone mid-study is noise they cannot act on.
      const where = Array.isArray(first?.loc) ? first.loc[0] : null
      if (where === 'body' && typeof first.msg === 'string') return tidy(first.msg)
    }
    if (typeof body.message === 'string' && body.message.trim()) return tidy(body.message)
  }
  if (status >= 500) return 'Something went wrong at our end. Please try again.'
  if (status === 404) return 'That is not available.'
  return 'That did not go through. Please try again.'
}

async function parseBody(res) {
  if (res.status === 204 || res.status === 205) return null
  const type = res.headers.get('content-type') || ''
  if (!type.includes('json')) {
    const text = await res.text().catch(() => '')
    return text || null
  }
  try {
    return await res.json()
  } catch {
    return null
  }
}

/**
 * @param {string} method
 * @param {string} path      path under /api, e.g. '/answers'
 * @param {object} [opts]
 * @param {*}      [opts.body]    JSON-serialised when present
 * @param {AbortSignal} [opts.signal]
 * @param {boolean} [opts.allowUnauthenticated]
 *        true for calls where "not logged in" is a legitimate answer rather than
 *        session loss (auth/me, login, register). Skips the global 401 handler.
 */
async function request(method, path, opts = {}) {
  const { body, signal, allowUnauthenticated = false, headers = {} } = opts
  const hasBody = body !== undefined && body !== null

  let res
  try {
    res = await fetch(BASE + path, {
      method,
      // same-origin cookie carries the JWT. Never 'omit', never a bearer header.
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: hasBody ? JSON.stringify(body) : undefined,
      signal,
    })
  } catch (err) {
    if (err && err.name === 'AbortError') throw err
    throw new ApiError(
      0,
      'network',
      "Can't reach the server. Check your connection and try again.",
    )
  }

  const parsed = await parseBody(res)

  if (res.ok) return parsed

  if (res.status === 401) {
    if (!allowUnauthenticated && unauthorizedHandler) unauthorizedHandler()
    /*
     * "Please sign in again." IS THE SESSION-LOSS MESSAGE, and it was being
     * used for every 401 — including the one raised BY the sign-in form.
     *
     * So a wrong password on the login screen said "Please sign in again." on
     * the screen she was already signing in on: it names the wrong act, blames
     * nothing she can act on, and reads as though something else broke.
     *
     * allowUnauthenticated already marks the calls where "not logged in" is a
     * legitimate answer rather than session loss. That flag was governing the
     * REDIRECT and not the WORDS, so the words stayed wrong on exactly the
     * calls the flag exists to distinguish. Now it governs both.
     */
    if (allowUnauthenticated) {
      throw new ApiError(401, 'unauthorized', messageFromBody(parsed, 401), parsed)
    }
    throw new ApiError(401, 'unauthorized', 'Please sign in again.', parsed)
  }

  throw new ApiError(res.status, 'http', messageFromBody(parsed, res.status), parsed)
}

export const api = {
  get: (path, opts) => request('GET', path, opts),
  post: (path, body, opts) => request('POST', path, { ...opts, body }),
  put: (path, body, opts) => request('PUT', path, { ...opts, body }),
  del: (path, opts) => request('DELETE', path, opts),
}

/* ------------------------------------------------------------------ *
 * Endpoint surface, from architecture.md. One named function per endpoint so
 * a contract change lands in one file. Nothing here interprets an answer.
 * ------------------------------------------------------------------ */

export const auth = {
  register: (payload) => api.post('/auth/register', payload, { allowUnauthenticated: true }),
  login: (payload) => api.post('/auth/login', payload, { allowUnauthenticated: true }),
  logout: () => api.post('/auth/logout', {}),
  me: () => api.get('/auth/me', { allowUnauthenticated: true }),

  /**
   * Stop the study. Irreversible from the participant's side and idempotent.
   *
   * The response carries the server's own wording for the ending screen. The
   * session cookie is deliberately left in place by backend, but every other
   * endpoint refuses a withdrawn participant, so nothing after this may depend
   * on a working session.
   */
  withdraw: () => api.post('/auth/withdraw', {}),
}

export const config = {
  /** One bundle stamped with config_version. Cached client-side by that version. */
  bundle: () => api.get('/config', { allowUnauthenticated: true }),
}

export const onboarding = {
  /** Consent also generates and stores all 30 beep rows, server side. */
  consent: (payload) => api.post('/onboarding/consent', payload),
  demographics: (payload) => api.put('/onboarding/demographics', payload),
}

export const day0 = {
  /** What is already answered plus what is next. Resume is a query, not a draft. */
  state: () => api.get('/day0/state'),
}

export const answers = {
  /**
   * One item, upsert. beep_id is null on day 0.
   * Called once per answered item, immediately. This is why closing the tab
   * loses nothing and why there is no draft anywhere in this client.
   *
   * Never call with a blank value: that is a 422, by design. Clearing an answer
   * is remove(), because a POST that deletes on blank turns one rendering bug
   * into silent data loss (backend decision #14).
   */
  save: (payload) => api.post('/answers', payload),

  /** Un-answer. 204 whether or not a row existed, so a retry is safe. */
  remove: (itemId, beepId = null) => {
    const params = new URLSearchParams({ item_id: itemId })
    if (beepId !== null && beepId !== undefined) params.set('beep_id', String(beepId))
    return api.del(`/answers?${params.toString()}`)
  },
}

export const beeps = {
  current: () => api.get('/beeps/current'),
  get: (id) => api.get(`/beeps/${encodeURIComponent(id)}`),
  open: (id) => api.post(`/beeps/${encodeURIComponent(id)}/open`, {}),
  submit: (id) => api.post(`/beeps/${encodeURIComponent(id)}/submit`, {}),
}

export const push = {
  /*
   * allowUnauthenticated, and it is a bug fix, not a loosening. App.jsx
   * re-asserts the subscription once per launch, gated only on notification
   * PERMISSION - which persists on a device long after the session cookie is
   * gone. A signed-out visitor opening /register on a phone that had granted
   * permission fired this POST, took the 401, and the GLOBAL handler bounced
   * her off the register form to /login mid-keystroke. An unauthenticated
   * subscribe attempt means "not signed in yet", not "session lost"; the
   * caller already treats failure as 'try again next launch'.
   */
  subscribe: (subscription) => api.post('/push/subscribe', subscription, { allowUnauthenticated: true }),
}

export const safety = {
  /**
   * Crisis copy and the resource list for a country.
   * Called with no argument the server uses the participant's own demo_country
   * and falls back to the default block, so the client never picks a country.
   */
  resources: (country) =>
    api.get(
      country ? `/safety/resources?country=${encodeURIComponent(country)}` : '/safety/resources',
      // allowUnauthenticated is NOT about this being a public endpoint — it is
      // not. It stops a 401 here from firing the global "go to login" handler.
      // This call happens at the one moment a crisis screen is due, and
      // bouncing someone to a sign-in form instead of showing them help,
      // because their cookie happened to lapse, is the worst possible time for
      // that redirect. The caller catches the failure and renders crisis
      // content from the config bundle instead.
      { allowUnauthenticated: true },
    ),

  /**
   * Record that the participant was SHOWN the crisis screen this answer raised
   * (backend decision #132).
   *
   * `acknowledged_at` was NULL on every safety_events row ever written, so the
   * study could say a RULE FIRED and could not say ANYONE SAW THE RESOURCES.
   * For a study that compels a disclosure of suicidal ideation those are not
   * the same fact, and the second is the one that answers whether the duty of
   * care was discharged.
   *
   * ONE HANDLE, DELIBERATELY. `SF01 = 4` matches three rules and writes three
   * rows; she is shown ONE screen, so there is one thing to acknowledge and the
   * server stamps every event that answer raised. This client is not told that
   * and must not reason about it — it holds the answer id and nothing else.
   *
   * FIRE AND FORGET. Not a gate: #119 settled that the level-3 dismissal is a
   * mis-tap guard rather than a record requirement, and #99 that an outcome is
   * never held. Nothing blocks on this, no label changes, and a failure must
   * never re-show the screen or start a retry loop.
   *
   * allowUnauthenticated for the same reason as `resources` above: a lapsed
   * cookie must not bounce someone to a sign-in form at the moment she is
   * dismissing a crisis screen.
   */
  acknowledge: (answerId) =>
    api.post('/safety/acknowledge', { answer_id: answerId }, { allowUnauthenticated: true }),
}
